import Content from '@ui/components/Content/Content';
import styles from '@ui/components/ProgressBar/ProgressBar.module.scss';
import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon/FontAwesomeProIcon';
import React, { useEffect, useRef } from 'react';
import { Button as BaseButton } from '@ariakit/react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const ProgressBar = ({
  steps,
  currentStep,
  noLabel = false,
  onClick,
  isNotInteractive = false,
}) => {
  const initialFocusRef = useRef(null);
  const onClickHandler = id => {
    if (onClick) {
      onClick(id);
    }
  };

  const getStepPosition = index => {
    switch (true) {
      case index + 1 < currentStep:
        return 'prevSteps';
      case index + 1 === currentStep:
        return 'currentStep';
      default:
        return 'followingSteps';
    }
  };

  useEffect(() => {
    if (steps?.some(step => step?.label) && steps?.length > 5) {
      // eslint-disable-next-line no-console
      console.error(
        'Steps array with labels may not contain more than 5 elements',
      );
    } else if (steps?.length > 13) {
      // eslint-disable-next-line no-console
      console.error('Steps array may not contain more than 13 elements');
    }
  }, [steps]);

  useEffect(() => {
    if (initialFocusRef.current) {
      initialFocusRef.current.focus();
    }
  }, [currentStep]);

  return (
    <div
      aria-valuemax={steps.length}
      aria-valuemin={1}
      aria-valuenow={currentStep}
      className={styles.progressBarContainer}
      role="progressbar"
    >
      <div className={styles.labelRowContainer}>
        {steps.map((step, index) => (
          <div className={styles.labelContainer} key={step.id}>
            {getStepPosition(index) === 'currentStep' && (
              <FontAwesomeProIcon
                className={styles.chevronDown}
                icon="chevron-down"
              />
            )}
            {step?.label && !noLabel && (
              <Content
                as="span"
                className={classNames(styles.label, {
                  [styles.activeLabel]:
                    getStepPosition(index) === 'prevSteps' ||
                    getStepPosition(index) === 'currentStep',
                })}
                noSpacing
              >
                {step.label}
              </Content>
            )}
          </div>
        ))}
      </div>
      <div className={styles.loadingBarRow}>
        {steps.map((step, index) => (
          <div className={styles.loadingBarSection} key={step.id}>
            <BaseButton
              aria-label={`Schritt ${step.id}`}
              className={classNames(styles.dot, {
                [styles.currentStep]: getStepPosition(index) === 'currentStep',
                [styles.doneStep]: getStepPosition(index) === 'prevSteps',
                [styles.noHover]: isNotInteractive,
              })}
              disabled={index + 1 > currentStep}
              onClick={() => onClickHandler(step.id)}
              ref={currentStep === index ? initialFocusRef : null}
              role="progressbar"
              tabIndex={getStepPosition(index) === 'currentStep' ? 0 : -1}
            >
              {getStepPosition(index) === 'prevSteps' ? (
                <FontAwesomeProIcon className={styles.check} icon="check" />
              ) : (
                step.id
              )}
            </BaseButton>
            {index + 1 !== steps.length && (
              <div
                className={classNames(styles.line, {
                  [styles.doneStep]: index + 1 < currentStep,
                })}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  currentStep: PropTypes.number.isRequired,
  isNotInteractive: PropTypes.bool,
  noLabel: PropTypes.bool,
  onClick: PropTypes.func,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string,
    }),
  ).isRequired,
};

export default ProgressBar;
